<!-- src/lib/components/BoardView.svelte -->
<script lang="ts">
  import { pmStore } from '../stores/pm.svelte.ts';
  import { agentStore } from '../stores/agents.svelte.ts';
  import { api } from '../api.ts';
  import type { Card, ColumnDetail } from '../types.ts';
  import CardItem from './CardItem.svelte';
  import EmptyState from './EmptyState.svelte';
  import { flip } from 'svelte/animate';
  import { slide, fly } from 'svelte/transition';

  interface Props {
    boardId: string;
    onOpenCard: (id: string) => void;
  }
  let { boardId, onOpenCard }: Props = $props();

  let newCardColumn = $state<string | null>(null);

  let shareUrl = $state<string | null>(null);
  let sharing  = $state(false);

  async function shareBoard() {
    sharing = true;
    try {
      const result = await api<{ token: string }>(`/api/v1/boards/${boardId}/share`, { method: 'POST' });
      shareUrl = `${window.location.origin}/share/${result.token}`;
    } catch {
      // silently ignore — could show an error toast here
    } finally {
      sharing = false;
    }
  }
  let newCardTitle  = $state('');
  let creating      = $state(false);

  let draggingCardId  = $state<string | null>(null);
  let draggingFromCol = $state<string | null>(null);
  let dragOverCol     = $state<string | null>(null);

  let selectedCardIds = $state(new Set<string>());

  let filterQuery    = $state('');
  let filterPriority = $state<string | null>(null);
  // Improvement #21: filter cards by agent vs human assignee
  let assigneeFilter = $state<'all' | 'agent' | 'human'>('all');
  type BoardGroupBy = 'none' | 'priority';
  const GROUP_BY_KEY = 'agentboard.boardGroupBy';
  const COLLAPSED_SWIMLANES_KEY = 'agentboard.boardPrioritySwimlanesCollapsed';

  function loadBoardGroupBy(): BoardGroupBy {
    try {
      const value = localStorage.getItem(GROUP_BY_KEY);
      return value === 'priority' ? 'priority' : 'none';
    } catch {
      return 'none';
    }
  }

  function loadCollapsedSwimlanes(): Set<string> {
    try {
      const raw = localStorage.getItem(COLLAPSED_SWIMLANES_KEY);
      if (!raw) return new Set();
      const values = JSON.parse(raw);
      return new Set(Array.isArray(values) ? values.filter((item) => typeof item === 'string') : []);
    } catch {
      return new Set();
    }
  }

  let groupBy = $state<BoardGroupBy>(loadBoardGroupBy());
  let collapsedSwimlanes = $state<Set<string>>(loadCollapsedSwimlanes());

  let detail = $derived(pmStore.boardDetails[boardId]);

  function cardMatchesFilter(card: { title: string; description?: string | null; priority: string; assignee_kind?: string | null }): boolean {
    if (filterPriority && card.priority !== filterPriority) return false;
    if (assigneeFilter === 'agent' && card.assignee_kind !== 'persona') return false;
    if (assigneeFilter === 'human' && card.assignee_kind === 'persona') return false;
    if (filterQuery) {
      const q = filterQuery.toLowerCase();
      return card.title.toLowerCase().includes(q) ||
             (card.description?.toLowerCase().includes(q) ?? false);
    }
    return true;
  }

  const filteredCardCount: number = $derived(
    detail ? detail.columns.flatMap(c => c.cards).filter(cardMatchesFilter).length : 0
  );

  const priorities = [
    { value: 'urgent', label: 'Urgent', color: 'oklch(0.62 0.26 25)' },
    { value: 'high',   label: 'High',   color: 'oklch(0.78 0.16 85)' },
    { value: 'medium', label: 'Medium', color: 'oklch(0.58 0.22 265)' },
    { value: 'low',    label: 'Low',    color: 'oklch(0.76 0.05 220 / 0.75)' },
    { value: 'none',   label: 'None',   color: 'oklch(0.56 0.03 310 / 0.55)' },
  ];

  $effect(() => {
    try {
      localStorage.setItem(GROUP_BY_KEY, groupBy);
    } catch {
      // Ignore storage failures; grouping should still work for the session.
    }
  });

  $effect(() => {
    try {
      localStorage.setItem(COLLAPSED_SWIMLANES_KEY, JSON.stringify([...collapsedSwimlanes]));
    } catch {
      // Ignore storage failures; collapse state is opportunistic.
    }
  });

  function laneCards(column: ColumnDetail, priority: Card['priority']): Card[] {
    return column.cards.filter((card) => card.priority === priority && cardMatchesFilter(card));
  }

  function toggleSwimlane(priority: string) {
    const next = new Set(collapsedSwimlanes);
    if (next.has(priority)) next.delete(priority);
    else next.add(priority);
    collapsedSwimlanes = next;
  }

  function handleCardSelect(cardId: string, selected: boolean) {
    const next = new Set(selectedCardIds);
    if (selected) {
      next.add(cardId);
    } else {
      next.delete(cardId);
    }
    selectedCardIds = next;
  }

  async function batchSetPriority(priority: string) {
    const ids = [...selectedCardIds];
    await Promise.all(ids.map(id =>
      api(`/api/v1/cards/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ priority }),
      }).catch(() => {})
    ));
    if (detail) {
      for (const col of detail.columns) {
        for (const card of col.cards) {
          if (selectedCardIds.has(card.id)) card.priority = priority as any;
        }
      }
    }
    selectedCardIds = new Set();
  }

  async function batchMoveToColumn(columnId: string) {
    const ids = [...selectedCardIds];
    await Promise.all(ids.map(id =>
      api(`/api/v1/cards/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ column_id: columnId }),
      }).catch(() => {})
    ));
    if (detail) {
      const movedCards: typeof detail.columns[0]['cards'] = [];
      for (const col of detail.columns) {
        const remaining = col.cards.filter(c => !selectedCardIds.has(c.id));
        const moved = col.cards.filter(c => selectedCardIds.has(c.id));
        col.cards = remaining;
        movedCards.push(...moved);
      }
      const targetCol = detail.columns.find(c => c.id === columnId);
      if (targetCol) {
        for (const c of movedCards) { c.column_id = columnId; }
        targetCol.cards.push(...movedCards);
      }
    }
    selectedCardIds = new Set();
  }

  async function batchDelete() {
    const ids = [...selectedCardIds];
    if (!confirm(`Delete ${ids.length} cards?`)) return;
    await Promise.all(ids.map(id =>
      api(`/api/v1/cards/${id}`, { method: 'DELETE' }).catch(() => {})
    ));
    if (detail) {
      for (const col of detail.columns) {
        col.cards = col.cards.filter(c => !selectedCardIds.has(c.id));
      }
    }
    selectedCardIds = new Set();
  }

  function handleBoardKeydown(e: KeyboardEvent) {
    const target = e.target as HTMLElement;
    const inInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

    if (e.key === 'Escape' && selectedCardIds.size > 0) {
      e.preventDefault();
      selectedCardIds = new Set();
    }
    if ((e.key === 'Delete' || e.key === 'Backspace') && selectedCardIds.size > 0 && !inInput) {
      e.preventDefault();
      batchDelete();
    }
  }

  async function createCard(columnId: string) {
    if (!newCardTitle.trim() || !detail) return;
    creating = true;
    try {
      await api('/api/v1/cards', {
        method: 'POST',
        body: JSON.stringify({
          project_id: detail.project_id,
          column_id: columnId,
          title: newCardTitle.trim(),
          priority: 'none',
        }),
      });
      newCardTitle = '';
      newCardColumn = null;
    } finally {
      creating = false;
    }
  }

  function onCardDragStart(e: DragEvent, cardId: string, colId: string) {
    draggingCardId  = cardId;
    draggingFromCol = colId;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', cardId);
    }
  }

  function onCardDragEnd() {
    draggingCardId  = null;
    draggingFromCol = null;
    dragOverCol     = null;
  }

  function onColDragOver(e: DragEvent, colId: string) {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    dragOverCol = colId;
  }

  function onColDragLeave(colId: string) {
    if (dragOverCol === colId) dragOverCol = null;
  }

  async function onColDrop(e: DragEvent, targetColId: string) {
    e.preventDefault();
    dragOverCol = null;
    if (!draggingCardId || draggingFromCol === targetColId) {
      draggingCardId  = null;
      draggingFromCol = null;
      return;
    }
    const cardId = draggingCardId;
    draggingCardId  = null;
    draggingFromCol = null;
    // Optimistic update: move card in local store
    if (detail) {
      for (const col of detail.columns) {
        const idx = col.cards.findIndex(c => c.id === cardId);
        if (idx !== -1) {
          const [card] = col.cards.splice(idx, 1);
          const target = detail.columns.find(c => c.id === targetColId);
          if (target) {
            card.column_id = targetColId;
            target.cards.push(card);
            // Track human-driven completions for the AgentStatusBar metric
            if (
              (target.name === 'Done' || target.name === 'done') &&
              card.assignee_kind !== 'persona'
            ) {
              agentStore.completedByHuman += 1;
            }
          }
          break;
        }
      }
    }
    // Persist to server
    await api(`/api/v1/cards/${cardId}`, {
      method: 'PATCH',
      body: JSON.stringify({ column_id: targetColId }),
    }).catch(() => {/* WebSocket will correct if fails */});
  }
</script>

<svelte:window onkeydown={handleBoardKeydown} />

{#if !detail}
  <EmptyState
    icon="board"
    title="No board selected"
    subtitle="Create a workspace and project to get started."
  />
{:else}
  <div class="board-wrapper">
    <!-- Board header bar -->
    <div class="board-header-bar">
      <div class="board-header-left">
        <span class="board-label">Board</span>
      </div>
      <div class="board-view-switcher">
        <button class="view-btn active">Board</button>
        <button class="view-btn">Backlog</button>
        <button class="view-btn">Calendar</button>
      </div>
      <div class="board-header-right">
        <button class="share-btn" onclick={shareBoard} disabled={sharing} title="Share board">
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
            <circle cx="10.5" cy="2.5" r="1.5" stroke="currentColor" stroke-width="1.2"/>
            <circle cx="10.5" cy="10.5" r="1.5" stroke="currentColor" stroke-width="1.2"/>
            <circle cx="2.5" cy="6.5" r="1.5" stroke="currentColor" stroke-width="1.2"/>
            <line x1="4" y1="6" x2="9" y2="3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
            <line x1="4" y1="7" x2="9" y2="10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
          </svg>
          Share
        </button>
      </div>
    </div>

    {#if shareUrl}
      <div class="share-toast" transition:fly={{ y: 10, duration: 150 }}>
        <span class="share-url">{shareUrl}</span>
        <button onclick={() => { navigator.clipboard.writeText(shareUrl!); shareUrl = null; }}>Copy link</button>
        <button onclick={() => { shareUrl = null; }}>✕</button>
      </div>
    {/if}

    <div class="board-toolbar">
      <div class="search-field">
        <svg class="search-icon" width="13" height="13" viewBox="0 0 13 13" fill="none">
          <circle cx="5.5" cy="5.5" r="3.5" stroke="currentColor" stroke-width="1.2"/>
          <line x1="8.5" y1="8.5" x2="11.5" y2="11.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
        </svg>
        <input
          id="board-filter"
          bind:value={filterQuery}
          placeholder="Filter cards…"
          class="filter-input"
          onkeydown={(e) => { if (e.key === 'Escape') { filterQuery = ''; filterPriority = null; } }}
        />
        {#if filterQuery}
          <button class="clear-filter" onclick={() => { filterQuery = ''; }}>✕</button>
        {/if}
      </div>

      <!-- Priority filter pills -->
      <div class="priority-filters">
        {#each [
          { value: 'urgent', label: 'Urgent', color: 'oklch(0.62 0.26 25)' },
          { value: 'high',   label: 'High',   color: 'oklch(0.78 0.16 85)' },
          { value: 'medium', label: 'Medium', color: 'oklch(0.58 0.22 265)' },
        ] as p}
          <button
            class="pri-filter"
            class:active={filterPriority === p.value}
            style="--fc: {p.color}"
            onclick={() => { filterPriority = filterPriority === p.value ? null : p.value; }}
          >
            <span class="pri-dot" style="background: {p.color}"></span>
            {p.label}
          </button>
        {/each}
      </div>

      <!-- Improvement #21: assignee type filter -->
      <div class="assignee-filter" role="group">
        {#each [['all', 'All'], ['agent', '🤖 Agents'], ['human', '👤 Humans']] as [val, label]}
          <button
            class="filter-chip"
            class:active={assigneeFilter === val}
            onclick={() => { assigneeFilter = val as typeof assigneeFilter; }}
          >{label}</button>
        {/each}
      </div>

      <div class="group-control">
        <span class="group-label">Group:</span>
        <select class="group-select" bind:value={groupBy}>
          <option value="none">None</option>
          <option value="priority">Priority</option>
        </select>
      </div>

      {#if filterQuery || filterPriority}
        <span class="filter-count">{filteredCardCount} cards</span>
        <button class="clear-all" onclick={() => { filterQuery = ''; filterPriority = null; }}>Clear filters</button>
      {/if}
    </div>

    <div class="board">
      {#each detail.columns as column (column.id)}
        <div
          class="column"
          role="region"
          ondragover={(e) => onColDragOver(e, column.id)}
          ondragleave={() => onColDragLeave(column.id)}
          ondrop={(e) => onColDrop(e, column.id)}
          class:drag-over={dragOverCol === column.id && draggingFromCol !== column.id}
        >
          <header class="col-header" style="--col-color: {column.color}">
            <div class="col-title-row">
              <span class="col-indicator"></span>
              <span class="col-name">{column.name}</span>
              <span class="col-count">{column.cards.length}</span>
            </div>
            <div class="col-actions">
              <button class="col-action-btn menu-btn" title="Column options">···</button>
              <button
                class="col-action-btn add-btn"
                onclick={() => { newCardColumn = newCardColumn === column.id ? null : column.id; newCardTitle = ''; }}
                title="Add card"
              >+</button>
            </div>
          </header>

          {#if newCardColumn === column.id}
            <form
              class="new-card-form"
              transition:slide={{ duration: 150 }}
              onsubmit={(e) => { e.preventDefault(); createCard(column.id); }}
            >
              <!-- svelte-ignore a11y_autofocus -->
              <textarea
                bind:value={newCardTitle}
                placeholder="Card title…"
                rows={2}
                disabled={creating}
                autofocus
              ></textarea>
              <div class="form-btns">
                <button type="submit" disabled={creating || !newCardTitle.trim()}>Add</button>
                <button type="button" onclick={() => { newCardColumn = null; newCardTitle = ''; }}>Cancel</button>
              </div>
            </form>
          {/if}

          {#if groupBy === 'priority'}
            <div class="swimlane-list">
              {#each priorities as priority (priority.value)}
                {@const swimlaneCards = laneCards(column, priority.value as Card['priority'])}
                {#if swimlaneCards.length > 0}
                  <section class="swimlane">
                    <button class="swimlane-header" type="button" onclick={() => toggleSwimlane(priority.value)}>
                      <span class="swimlane-meta">
                        <span class="swimlane-chevron" class:collapsed={collapsedSwimlanes.has(priority.value)}>▾</span>
                        <span class="pri-dot" style="background: {priority.color}"></span>
                        <span class="swimlane-name">{priority.label}</span>
                      </span>
                      <span class="swimlane-count">{swimlaneCards.length}</span>
                    </button>
                    {#if !collapsedSwimlanes.has(priority.value)}
                      <div class="card-list swimlane-card-list">
                        {#each swimlaneCards as card (card.id)}
                          <div
                            animate:flip={{ duration: 200 }}
                            transition:slide={{ duration: 150 }}
                            draggable={true}
                            role="button"
                            tabindex="0"
                            ondragstart={(e) => onCardDragStart(e, card.id, column.id)}
                            ondragend={onCardDragEnd}
                            class:dragging={draggingCardId === card.id}
                          >
                            <CardItem {card} onOpen={onOpenCard} columnName={column.name} {selectedCardIds} onSelect={handleCardSelect} />
                          </div>
                        {/each}
                      </div>
                    {/if}
                  </section>
                {/if}
              {/each}
            </div>
          {:else}
            <div class="card-list">
              {#each column.cards as card (card.id)}
                <div
                  animate:flip={{ duration: 200 }}
                  transition:slide={{ duration: 150 }}
                  draggable={true}
                  role="button"
                  tabindex="0"
                  ondragstart={(e) => onCardDragStart(e, card.id, column.id)}
                  ondragend={onCardDragEnd}
                  class:dragging={draggingCardId === card.id}
                  class:card-hidden={!cardMatchesFilter(card)}
                >
                  <CardItem {card} onOpen={onOpenCard} columnName={column.name} {selectedCardIds} onSelect={handleCardSelect} />
                </div>
              {/each}
            </div>
          {/if}
        </div>
      {/each}
    </div>

    {#if selectedCardIds.size > 0}
      <div class="batch-bar">
        <span class="batch-count">{selectedCardIds.size} selected</span>

        <!-- Priority batch -->
        <div class="batch-group">
          {#each priorities as p}
            <button class="batch-btn" onclick={() => batchSetPriority(p.value)} title="Set priority: {p.label}">
              <span class="p-dot" style="background: {p.color}"></span>
              {p.label}
            </button>
          {/each}
        </div>

        <!-- Column batch (move to column) -->
        <div class="batch-group">
          {#each detail.columns as col}
            <button class="batch-btn" onclick={() => batchMoveToColumn(col.id)} title="Move to: {col.name}">
              → {col.name}
            </button>
          {/each}
        </div>

        <!-- Delete batch -->
        <button class="batch-btn batch-danger" onclick={batchDelete} title="Delete selected">
          Delete
        </button>

        <!-- Clear selection -->
        <button class="batch-btn batch-clear" onclick={() => { selectedCardIds = new Set(); }}>
          ✕ Clear
        </button>
      </div>
    {/if}
  </div>
{/if}

<style>
  .board-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  /* Board header bar */
  .board-header-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1.25rem;
    border-bottom: 1px solid rgba(255,255,255,0.05);
    background: rgba(255,255,255,0.01);
    flex-shrink: 0;
  }
  .board-header-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .board-label {
    font-size: 0.75rem;
    color: rgba(255,255,255,0.35);
    font-weight: 500;
    letter-spacing: 0.02em;
  }
  .board-view-switcher {
    display: flex;
    align-items: center;
    gap: 2px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 6px;
    padding: 2px;
  }
  .view-btn {
    background: transparent;
    border: none;
    color: rgba(255,255,255,0.3);
    padding: 0.2rem 0.6rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.72rem;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .view-btn.active {
    background: rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.7);
  }
  .view-btn:hover:not(.active) {
    color: rgba(255,255,255,0.5);
    background: rgba(255,255,255,0.04);
  }

  .board {
    display: flex;
    gap: var(--col-gap, 1rem);
    padding: 1.25rem;
    overflow-x: auto;
    flex: 1;
    align-items: flex-start;
    background:
      radial-gradient(ellipse at 18% 55%, oklch(0.28 0.18 310 / 0.055) 0%, transparent 60%),
      radial-gradient(ellipse at 82% 28%, oklch(0.28 0.16 265 / 0.045) 0%, transparent 60%),
      radial-gradient(ellipse at 50% 92%, oklch(0.32 0.14 85  / 0.025) 0%, transparent 60%);
    animation: bg-breathe calc(var(--pulse-rate, 10s) * 1.8) ease-in-out infinite;
  }
  @keyframes bg-breathe {
    0%, 100% { opacity: 1;    }
    50%       { opacity: 0.88; }
  }

  /* Column: opaque panel — NO backdrop-filter, NO mix-blend-mode.
     Inside bioluminescence comes from a ::before pseudo that animates
     opacity only. contain: layout paint style isolates each column's
     compositing so card hovers don't force re-layout of neighbors. */
  .column {
    flex-shrink: 0;
    width: 300px;
    background: linear-gradient(180deg,
      oklch(0.13 0.02 275) 0%,
      oklch(0.10 0.02 275) 100%);
    border: 1px solid oklch(0.30 0.05 275 / 0.18);
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    gap: var(--card-gap, 0.5rem);
    overflow: hidden;
    position: relative;
    box-shadow: inset 0 0 60px oklch(0.30 0.12 310 / 0.04);
    transition: border-color 100ms ease;
    contain: layout style paint;
  }
  /* Column top glow: normal compositing, no mix-blend-mode.
     mix-blend-mode: screen was creating a stacking context per column
     (5+ simultaneous blend contexts) which forced software compositing
     during card hovers. The radial gradient still reads as a soft
     colored bloom at the top of the column without the blend mode. */
  .column::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    border-radius: inherit;
    background: radial-gradient(
      ellipse 80% 40% at 50% 0%,
      var(--col-color, oklch(0.72 0.28 310)) 0%,
      transparent 72%);
    opacity: 0.12;
    will-change: opacity;
    animation: column-breathe calc(var(--pulse-rate, 10s) * 1.2) ease-in-out infinite;
  }
  @keyframes column-breathe {
    0%, 100% { opacity: 0.09; }
    50%       { opacity: 0.18; }
  }
  .column:hover {
    border-color: oklch(0.72 0.28 310 / 0.28);
  }
  .col-header {
    padding: 0.75rem 0.875rem 0.5rem;
    border-bottom: 1px solid rgba(255,255,255,0.05);
    border-top: 2px solid var(--col-color);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .col-title-row { display: flex; align-items: center; gap: 0.45rem; }
  .col-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--col-color);
    box-shadow: 0 0 6px var(--col-color);
    flex-shrink: 0;
  }
  .col-name {
    font-size: 0.8rem;
    color: rgba(255,255,255,0.65);
    font-weight: 500;
    letter-spacing: 0.01em;
  }
  .col-count {
    font-size: 0.68rem;
    background: rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.4);
    border-radius: 8px;
    padding: 0.05rem 0.4rem;
    font-family: 'JetBrains Mono', monospace;
  }

  /* Column action buttons — visible on column hover */
  .col-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    opacity: 0;
    transition: opacity 150ms ease;
  }
  .column:hover .col-actions {
    opacity: 1;
  }
  .col-action-btn {
    background: transparent;
    border: 1px solid rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.3);
    width: 22px;
    height: 22px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.85rem;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
    padding: 0;
  }
  .col-action-btn:hover {
    border-color: var(--col-color);
    color: var(--col-color);
  }
  .menu-btn {
    font-size: 0.75rem;
    letter-spacing: 0.05em;
  }

  .new-card-form {
    padding: 0.5rem 0.75rem 0;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }
  .new-card-form textarea {
    background: rgba(255,255,255,0.04);
    border: 1px solid oklch(0.72 0.28 310 / 0.26);
    color: #d0d4e0;
    padding: 0.45rem 0.6rem;
    border-radius: 5px;
    font-size: 0.82rem;
    resize: none;
    outline: none;
    width: 100%;
    box-sizing: border-box;
    font-family: system-ui, sans-serif;
    line-height: 1.4;
  }
  .form-btns { display: flex; gap: 0.375rem; }
  .form-btns button {
    flex: 1;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.4);
    padding: 0.3rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.75rem;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .form-btns button[type="submit"] {
    background: oklch(0.72 0.28 310 / 0.12);
    border-color: oklch(0.72 0.28 310 / 0.36);
    color: oklch(0.80 0.26 310);
  }
  .form-btns button[type="submit"]:hover:not(:disabled) { background: oklch(0.72 0.28 310 / 0.22); }
  .form-btns button:disabled { opacity: 0.4; cursor: not-allowed; }
  .card-list {
    display: flex;
    flex-direction: column;
    gap: var(--card-gap, 0.4rem);
    padding: 0.25rem var(--col-padding, 0.625rem) 0.75rem;
    min-height: 40px;
  }
  .swimlane-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.25rem var(--col-padding, 0.625rem) 0.75rem;
  }
  .swimlane {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    border: 1px solid oklch(0.30 0.05 275 / 0.20);
    border-radius: 8px;
    background: oklch(0.11 0.02 275 / 0.75);
    overflow: hidden;
  }
  .swimlane-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    width: 100%;
    background: oklch(0.14 0.03 275 / 0.95);
    border: none;
    color: oklch(0.84 0.05 310 / 0.82);
    padding: 0.45rem 0.6rem;
    cursor: pointer;
    text-align: left;
  }
  .swimlane-header:hover {
    background: oklch(0.18 0.04 275 / 0.98);
  }
  .swimlane-meta {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 0;
  }
  .swimlane-chevron {
    display: inline-block;
    color: oklch(0.60 0.04 310 / 0.68);
    transition: transform 120ms ease;
  }
  .swimlane-chevron.collapsed {
    transform: rotate(-90deg);
  }
  .swimlane-name {
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.02em;
  }
  .swimlane-count {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.64rem;
    color: oklch(0.60 0.04 310 / 0.75);
    background: oklch(0.22 0.03 275 / 0.55);
    border-radius: 999px;
    padding: 0.08rem 0.35rem;
    flex-shrink: 0;
  }
  .swimlane-card-list {
    padding: 0 0.45rem 0.55rem;
  }

  /* ── Drag-and-drop visual feedback (refinement #20) ───────────── */

  /* Column drop zone: dotted border + subtle glow */
  .column.drag-over {
    background: oklch(0.72 0.28 310 / 0.04);
    border: 2px dashed oklch(0.72 0.28 310 / 0.35);
    border-radius: 8px;
  }
  .column.drag-over .card-list,
  .column.drag-over .swimlane-card-list {
    min-height: 80px;
  }

  /* The card being dragged: rotation + lifted shadow + reduced opacity */
  :global(.dragging) {
    opacity: 0.75;
    cursor: grabbing;
    transform: rotate(2deg) scale(1.02);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.45),
                0 0 0 2px oklch(0.72 0.28 310 / 0.25);
    z-index: 20;
  }

  /* Grab cursor on idle cards */
  :global(.card-item) {
    cursor: grab;
  }

  /* Batch action bar */
  .batch-bar {
    position: fixed;
    bottom: 1.5rem;
    left: 50%;
    transform: translateX(-50%);
    background: #1a1c24;
    border: 1px solid oklch(0.72 0.28 310 / 0.26);
    border-radius: 10px;
    padding: 0.625rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    box-shadow: 0 8px 32px rgba(0,0,0,0.6), 0 0 20px oklch(0.72 0.28 310 / 0.07);
    z-index: 30;
    flex-wrap: wrap;
  }
  .batch-count {
    font-size: 0.75rem;
    color: oklch(0.82 0.26 310 / 0.78);
    font-family: 'JetBrains Mono', monospace;
    white-space: nowrap;
    margin-right: 0.25rem;
  }
  .batch-group { display: flex; gap: 0.375rem; align-items: center; }
  .batch-btn {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.5);
    padding: 0.25rem 0.6rem;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.72rem;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
  .batch-btn:hover { background: rgba(255,255,255,0.08); color: #e8eaf0; border-color: rgba(255,255,255,0.2); }
  .batch-danger { border-color: rgba(255,80,80,0.25); color: rgba(255,80,80,0.6); }
  .batch-danger:hover { background: rgba(255,80,80,0.1); border-color: rgba(255,80,80,0.4); color: #ff5050; }
  .batch-clear { border-color: rgba(255,255,255,0.06); color: rgba(255,255,255,0.3); }
  .p-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

  /* Filter toolbar */
  .board-toolbar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 0.75rem;
    background: rgba(255,255,255,0.015);
    border-bottom: 1px solid rgba(255,255,255,0.04);
    flex-shrink: 0;
    flex-wrap: wrap;
  }
  .search-field {
    display: flex; align-items: center; gap: 0.35rem;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07);
    border-radius: 5px; padding: 0.25rem 0.5rem;
    transition: border-color 150ms ease;
  }
  .search-field:focus-within { border-color: oklch(0.72 0.28 310 / 0.26); }
  .search-icon { color: rgba(255,255,255,0.2); flex-shrink: 0; }
  .filter-input {
    background: transparent; border: none; outline: none;
    color: #d0d4e0; font-size: 0.78rem; width: 160px;
  }
  .filter-input::placeholder { color: rgba(255,255,255,0.2); }
  .clear-filter { background: none; border: none; color: rgba(255,255,255,0.2); cursor: pointer; font-size: 0.7rem; padding: 0; }
  .priority-filters { display: flex; gap: 0.25rem; }

  /* Improvement #21: assignee filter toggle */
  .assignee-filter {
    display: flex; gap: 0; border-radius: 6px; overflow: hidden;
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
  }
  .filter-chip {
    background: transparent;
    border: none;
    color: oklch(0.70 0.05 310 / 0.75);
    padding: 0.28rem 0.6rem;
    font-size: 0.72rem; cursor: pointer;
    transition: background 100ms, color 100ms;
  }
  .filter-chip + .filter-chip { border-left: 1px solid oklch(0.30 0.05 275 / 0.25); }
  .filter-chip:hover { background: oklch(0.72 0.28 310 / 0.08); color: oklch(0.90 0.10 310); }
  .filter-chip.active { background: oklch(0.72 0.28 310 / 0.18); color: oklch(0.96 0.12 310); }

  .group-control {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    margin-left: auto;
  }
  .group-label {
    font-size: 0.68rem;
    color: rgba(255,255,255,0.25);
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .group-select {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 5px;
    color: #d0d4e0;
    font-size: 0.72rem;
    padding: 0.28rem 1.4rem 0.28rem 0.5rem;
    outline: none;
  }
  .group-select:focus {
    border-color: oklch(0.72 0.28 310 / 0.30);
  }
  .pri-filter {
    display: flex; align-items: center; gap: 0.25rem;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07);
    color: rgba(255,255,255,0.35); padding: 0.2rem 0.5rem; border-radius: 4px;
    font-size: 0.68rem; cursor: pointer; transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .pri-filter:hover { border-color: var(--fc); color: rgba(255,255,255,0.6); }
  .pri-filter.active { border-color: var(--fc); color: #e8eaf0; background: rgba(255,255,255,0.06); }
  .pri-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
  .filter-count { font-size: 0.68rem; color: rgba(255,255,255,0.25); font-family: 'JetBrains Mono', monospace; }
  .clear-all { background: none; border: none; color: rgba(255,80,80,0.4); cursor: pointer; font-size: 0.68rem; transition: color 120ms ease; }
  .clear-all:hover { color: rgba(255,80,80,0.7); }

  /* Hidden cards (filtered out) */
  .card-hidden { display: none; }

  /* Share button */
  .board-header-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .share-btn {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.4);
    padding: 0.22rem 0.65rem;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.72rem;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .share-btn:hover:not(:disabled) {
    border-color: oklch(0.55 0.20 265 / 0.35);
    color: oklch(0.55 0.20 265 / 0.80);
  }
  .share-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  /* Share URL toast */
  .share-toast {
    position: fixed;
    bottom: 1.5rem;
    right: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: #1a1c24;
    border: 1px solid oklch(0.55 0.20 265 / 0.32);
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    box-shadow: 0 8px 32px rgba(0,0,0,0.5), 0 0 20px oklch(0.55 0.20 265 / 0.08);
    z-index: 40;
    max-width: calc(100vw - 3rem);
  }
  .share-url {
    font-size: 0.72rem;
    font-family: 'JetBrains Mono', monospace;
    color: rgba(255,255,255,0.5);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 320px;
  }
  .share-toast button {
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.5);
    padding: 0.2rem 0.55rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.72rem;
    white-space: nowrap;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
    flex-shrink: 0;
  }
  .share-toast button:hover { color: #e8eaf0; border-color: rgba(255,255,255,0.2); }
  .share-toast button:first-of-type {
    background: oklch(0.55 0.20 265 / 0.14);
    border-color: oklch(0.55 0.20 265 / 0.32);
    color: oklch(0.55 0.20 265 / 0.80);
  }
  .share-toast button:first-of-type:hover {
    background: oklch(0.55 0.20 265 / 0.18);
    color: oklch(0.58 0.22 265);
  }
</style>
